import React, { useMemo } from 'react';
import { Card, Skeleton } from 'antd';
import { UserOutlined } from '@ant-design/icons';

interface EstimatedReachProps {
    estimatedReach: number | null;
    loading?: boolean;
}

export const EstimatedReach: React.FC<EstimatedReachProps> = ({
    estimatedReach,
    loading = false,
}) => {
    // Memoized formatting logic
    const formattedReach = useMemo(() => {
        if (!estimatedReach) return null;

        if (estimatedReach >= 1000000) {
            return {
                value: (estimatedReach / 1000000).toFixed(1),
                unit: 'Million'
            };
        } else if (estimatedReach >= 1000) {
            return {
                value: (estimatedReach / 1000).toFixed(1),
                unit: 'Thousand'
            };
        } else {
            return {
                value: estimatedReach.toLocaleString(),
                unit: ''
            };
        }
    }, [estimatedReach]);

    // Determine state
    const isCalculating = loading || (!estimatedReach && estimatedReach !== 0);
    const hasData = !isCalculating && estimatedReach !== null;
    const isEmpty = !isCalculating && (estimatedReach === null || estimatedReach === 0);

    return (
        <Card
            title={
                <div className="flex items-center gap-2">
                    <span className="font-semibold">Estimated Reach</span>
                </div>
            }
            bordered={false}
            className="mt-4 w-full shadow-sm hover:shadow-md transition-shadow"
        >
            <div className="flex flex-col items-center py-6">
                {/* Loading State */}
                {isCalculating && (
                    <div className="w-full space-y-3">
                        <Skeleton.Input active size="small" className="w-full" />
                    </div>
                )}

                {/* Data State */}
                {hasData && formattedReach && (
                    <div className="text-center space-y-2 animate-fade-in">
                        <div className="flex items-center justify-center gap-2 mb-2">
                            <UserOutlined className="text-2xl text-blue-500" />
                        </div>
                        <div className="text-3xl font-bold text-gray-800">
                            {formattedReach.value}
                            {formattedReach.unit && (
                                <span className="text-lg font-medium text-gray-600 ml-1">
                                    {formattedReach.unit}
                                </span>
                            )}
                        </div>
                        <div className="text-sm font-medium text-gray-600">
                            Daily Active Users
                        </div>
                    </div>
                )}

                {/* Empty State */}
                {isEmpty && (
                    <div className="text-center space-y-2 text-gray-400">
                        <UserOutlined className="text-4xl opacity-50" />
                        <div className="text-sm">No reach data available</div>
                    </div>
                )}
            </div>

            {/* Disclaimer */}
            <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-500 text-center leading-relaxed max-w-xs mx-auto">
                    The accuracy of these estimates relies on factors like past campaign data,
                    budget, market data, targeting, and ad placements. They provide an idea of
                    potential performance but do not guarantee results.
                </p>
            </div>

            <style>{`
                @keyframes fade-in {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .animate-fade-in {
                    animation: fade-in 0.3s ease-out;
                }
            `}</style>
        </Card>
    );
};